import styled from 'styled-components';
import React from 'react';
import { getPublicAsset } from 'base/shared/Utils';
import { translate } from 'base/shared/Localization';

const Container = styled.div`
  background-color: #ffffff;
  padding: 4rem 2rem;
`;

const MainContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  color: #111827;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    font-size: 2.5rem;
    text-align: center;
  }
`;

const ContentContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 4rem;
  align-items: start;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const ImageContainer = styled.div`
  position: relative;
`;

const MainImage = styled.div`
  width: 100%;
  height: 500px;
  background-image: ${({ image }) => `url(${getPublicAsset(`v2/images/${image}`)})`};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 1.5rem;

  @media (max-width: 768px) {
    height: 400px;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
`;

const Icon = styled.div`
  width: 36px;
  height: 36px;
  background-image: ${({ image }) => `url(${getPublicAsset(`v2/images/${image}`)})`};
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  flex-shrink: 0;
  margin-top: 2px;
`;

const FeatureText = styled.p`
  font-size: 1rem;
  color: #374151;
  line-height: 1.5;
  margin: 0;
  font-weight: 400;
`;

const LifestyleSection = styled.section`
  margin-top: 5rem;
  padding: 0 2rem 4rem 2rem;
`;

const LifestyleTitle = styled.h2`
  font-size: 2rem;
  font-weight: bold;
  color: #111827;
  margin-bottom: 3rem;
  text-align: left;
  @media (max-width: 768px) {
    font-size: 2rem;
    text-align: center;
  }
`;

const LifestyleGrid = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 2rem;
  flex-wrap: wrap;
  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: center;
    gap: 2.5rem;
  }
`;

const LifestyleItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 270px;
  flex: 1 1 0;
`;

const LifestyleImage = styled.div`
  width: 180px;
  height: 180px;
  border-radius: 50%;
  background-image: ${({ image }) => `url(${getPublicAsset(`v2/images/${image}`)})`};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  margin-bottom: 2rem;
`;

const LifestyleLabel = styled.h3`
  font-size: 1.6rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 1rem;
  text-align: center;
`;

const LifestyleDesc = styled.p`
  font-size: 1.2rem;
  color: #222;
  text-align: center;
  line-height: 1.5;
`;

export default function ComfortSection() {
  const features = [
    { key: 'v2.comfortSection.features.0', icon: 'location.svg' },
    { key: 'v2.comfortSection.features.1', icon: 'smartlock.svg' },
    { key: 'v2.comfortSection.features.2', icon: 'receipt.svg' },
    { key: 'v2.comfortSection.features.3', icon: 'community.svg' },
    { key: 'v2.comfortSection.features.4', icon: 'creditcard.svg' },
    { key: 'v2.comfortSection.features.5', icon: 'calendar.svg' },
    { key: 'v2.comfortSection.features.6', icon: 'guestarea.svg' },
    { key: 'v2.comfortSection.features.7', icon: 'guestcare.svg' },
  ];

  return (
    <Container>
      <MainContent>
        <Title>{translate('v2.comfortSection.title')}</Title>

        <ContentContainer>
          <ImageContainer>
            <MainImage image="woman-reading.png" />
          </ImageContainer>

          <FeaturesGrid>
            {features.map((feature, index) => (
              <FeatureItem key={index}>
                <Icon image={feature.icon} />
                <FeatureText>{translate(feature.key)}</FeatureText>
              </FeatureItem>
            ))}
          </FeaturesGrid>
        </ContentContainer>
      </MainContent>
      {/* Lifestyle Section */}
      <LifestyleSection>
        <LifestyleTitle>{translate('v2.comfortSection.lifestyle.title')}</LifestyleTitle>
        <LifestyleGrid>
          <LifestyleItem>
            <LifestyleImage image="lifestyle-digital-nomad.png" />
            <LifestyleLabel>
              {translate('v2.comfortSection.lifestyle.digitalNomad.label')}
            </LifestyleLabel>
            <LifestyleDesc>
              {translate('v2.comfortSection.lifestyle.digitalNomad.desc')}
            </LifestyleDesc>
          </LifestyleItem>
          <LifestyleItem>
            <LifestyleImage image="lifestyle-students.png" />
            <LifestyleLabel>
              {translate('v2.comfortSection.lifestyle.students.label')}
            </LifestyleLabel>
            <LifestyleDesc>{translate('v2.comfortSection.lifestyle.students.desc')}</LifestyleDesc>
          </LifestyleItem>
          <LifestyleItem>
            <LifestyleImage image="lifestyle-couples.png" />
            <LifestyleLabel>
              {translate('v2.comfortSection.lifestyle.couples.label')}
            </LifestyleLabel>
            <LifestyleDesc>{translate('v2.comfortSection.lifestyle.couples.desc')}</LifestyleDesc>
          </LifestyleItem>
          <LifestyleItem>
            <LifestyleImage image="lifestyle-travelers.png" />
            <LifestyleLabel>
              {translate('v2.comfortSection.lifestyle.travelers.label')}
            </LifestyleLabel>
            <LifestyleDesc>{translate('v2.comfortSection.lifestyle.travelers.desc')}</LifestyleDesc>
          </LifestyleItem>
        </LifestyleGrid>
      </LifestyleSection>
    </Container>
  );
}
